import { helpers, React, ts, ui, useSelector } from '_core';

import { Params } from './types';
import { getSignalsObj, prepareLinearData } from '../../common/utils/signal-set-utils';

type ChartProps = {
  id: string;
  readOnly: boolean;
  loadWidgetData?: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  file: string;
  fullScreen: boolean;
  params: Params;
  context: ts.types.signalSet.SignalSetAnalysisContext;
  title: string;
  subtitle: string;
  setExternalEmbed: (_json: ts.types.analysis.EmbedChartData) => void;
};

const Chart = ({
  id,
  readOnly,
  loadWidgetData,
  file,
  fullScreen,
  context,
  params,
  title,
  subtitle,
  setExternalEmbed,
}: ChartProps) => {
  const [data, setData] = React.useState<ts.types.widgets.TableData>(null);
  const [alert, setAlert] = React.useState<{ type: 'error' | 'warning'; message: string } | null>(null);

  const resources = useSelector((state) => state.resources);
  const signalsObj = React.useMemo(() => getSignalsObj(context), [context]);

  const loadData = async () => {
    try {
      const response = await loadWidgetData({
        data: {
          file: file,
        },
      });
      if (response) {
        setData(prepareLinearData(response.data, resources, signalsObj, params.pipelines_display));
      }
    } catch (err) {
      setAlert({
        type: 'error',
        message: helpers.parseApiError(err as ts.types.common.ApiError),
      });
    }
  };

  React.useEffect(() => {
    if (file) loadData();
    return () => setData(null);
  }, [file]);

  return (
    <ui.LineChart
      id={id}
      data={data}
      fullScreen={fullScreen}
      readOnly={readOnly}
      alert={alert}
      showEndValueLabel
      showPeriodSelector
      exportTitle={title}
      exportSubTitle={subtitle}
      numberFormat="#.##%"
      setExternalEmbed={setExternalEmbed}
    />
  );
};

export default Chart;
